// client/src/services/apiClient.js
import axios from 'axios';

const BASE_URL = import.meta.env.VITE_BASE_URL;

const getApiBaseUrl = () =>
  window.location.hostname === 'localhost'
    ? 'http://localhost:7080/api'
    : `/api`;

const getAppApiUrl = () =>
  window.location.hostname === 'localhost'
    ? 'http://localhost:3001'
    : `/api`;

// ─── Access token expiry tracking (in-memory only — token itself lives in httpOnly cookie) ───
let accessTokenExpiresAt = null;

export const setAccessTokenExpiry = (expiresInSeconds) => {
  if (typeof expiresInSeconds === 'number' && expiresInSeconds > 0) {
    accessTokenExpiresAt = Date.now() + expiresInSeconds * 1000;
  }
};

export const getAccessTokenExpiresAt = () => accessTokenExpiresAt;

export const clearAccessTokenExpiry = () => {
  accessTokenExpiresAt = null;
};

// ─── Force logout (only when refresh token is truly dead) ───
let isLoggingOut = false;

export const forceLogoutAndRedirect = () => {
  if (isLoggingOut) return;
  isLoggingOut = true;
  console.error('🔒 Refresh token expired or invalid — forcing logout');

  clearAccessTokenExpiry();
  localStorage.removeItem('currentUser');

  // Primary: Electron IPC broadcast to other windows
  if (window.electronAPI && window.electronAPI.notifySessionChanged) {
    window.electronAPI.notifySessionChanged('session-logout', null);
  }

  // Fallback: BroadcastChannel for browser tabs
  try {
    const channel = new BroadcastChannel('bozarth-session-sync');
    channel.postMessage({ type: 'session-logout' });
    channel.close();
  } catch { /* BroadcastChannel may not be available */ }

  setTimeout(() => {
    isLoggingOut = false;
    // Footer stays on its route and reloads (Footer.jsx handles login mode);
    // dashboard navigates to the login page.
    if (window.location.pathname === '/footer') {
      window.location.reload();
    } else {
      window.location.href = '/login';
    }
  }, 100);
};

// ─── Token refresh logic (shared across everything) ───
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  failedQueue = [];
};

/**
 * Calls /auth/refresh. Server reads refresh token from httpOnly cookie and
 * sets a new access token cookie. Nothing token-shaped is exposed to JS.
 * On success: dispatches 'tokenRefreshed' so the WS hook can reconnect.
 * On failure: caller decides whether to force logout.
 */
export const attemptTokenRefresh = async () => {
  // Use raw axios (no interceptors) to avoid infinite loops
  const response = await axios.post(`${getApiBaseUrl()}/refresh`, null, {
    withCredentials: true,
  });

  if (response.data.success) {
    setAccessTokenExpiry(response.data.expiresIn);
    console.info('✅ Access token refreshed');

    window.dispatchEvent(new CustomEvent('tokenRefreshed'));
    return true;
  }

  throw new Error('Refresh failed');
};

// ─── Shared interceptor factory ───
const attachInterceptors = (client) => {
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Session was revoked server-side (admin force-logout / logout-all). A
      // refresh would also be rejected, so skip it and drop straight to login.
      if (error.response?.data?.revoked) {
        forceLogoutAndRedirect();
        return Promise.reject(error);
      }

      if (
        error.response &&
        (error.response.status === 401 || error.response.status === 403) &&
        !originalRequest._retry
      ) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(() => client(originalRequest))
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          await attemptTokenRefresh();
          processQueue(null);
          return client(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError);
          forceLogoutAndRedirect();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};

// ─── Axios instances ───
export const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});
attachInterceptors(apiClient);

export const appApiClient = axios.create({
  baseURL: getAppApiUrl(),
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});
attachInterceptors(appApiClient);

// ─── fetchWithAuth — tries refresh once on 401/403 ───
export const fetchWithAuth = async (url, options = {}) => {
  const makeRequest = () => fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });

  let response = await makeRequest();

  if (response.status === 401 || response.status === 403) {
    try {
      await attemptTokenRefresh();
      response = await makeRequest();

      if (response.status === 401 || response.status === 403) {
        forceLogoutAndRedirect();
        throw new Error('Authentication failed');
      }
    } catch (refreshError) {
      forceLogoutAndRedirect();
      throw new Error('Authentication failed');
    }
  }

  return response;
};
